import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FaUserFriends, FaShip, FaCalendarAlt, FaRulerCombined, FaMoneyBillWave, FaCheck } from 'react-icons/fa';
import { GiCaptainHatProfile, GiAnchor } from 'react-icons/gi';
import { IoMdResize } from "react-icons/io";

const YachtCard = ({ yacht, selectedDates }) => {
  console.log("YachtCard props:", yacht, selectedDates); // Debugging line to check props
  console.log('/yachts/' + yacht._id || yacht.stringId); // Debugging line to check URL
  const navigate = useNavigate();

  // Basic validation for yacht object
  if (!yacht || typeof yacht !== 'object') {
    console.error("Invalid yacht data passed to YachtCard:", yacht);
    return <div className="text-red-500 p-4">Invalid Yacht Data</div>; // Render an error or null
  }

  const handleCardClick = () => {
    // Navigate to a dynamic route like /yachts/:yachtId
    // Or pass state if preferred and the route is set up for it
    navigate(`/yachts/${yacht._id || yacht.stringId}`, { // Use yacht ID in URL
      state: {
        yacht, // Pass the full yacht object if needed on the details page
        dates: selectedDates
      }
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleButtonClick = (e) => {
    e.stopPropagation(); // Prevent card click when button is clicked
    handleCardClick(); // Reuse the navigation logic
  };
  // Find the lowest price from all cruise options and seasons
  const findLowestPrice = () => {
    if (!yacht.pricing) return null;
    
    let lowestPrice = Infinity;
    let priceType = '';
    
    // Helper to check and update lowest price
    const checkPrice = (price, type) => {
      if (price && !isNaN(parseFloat(price)) && parseFloat(price) < lowestPrice) {
        lowestPrice = parseFloat(price);
        priceType = type;
      }
    };

    // Helper to get lowest price from guest tiers
    const checkGuestTiers = (tiers, type) => {
      if (Array.isArray(tiers) && tiers.length > 0) {
        // Find the lowest rate in the tiers
        const lowestTierRate = Math.min(...tiers.map(tier => 
          typeof tier.rate === 'string' ? parseFloat(tier.rate) : tier.rate
        ).filter(rate => !isNaN(rate)));
        
        if (!isNaN(lowestTierRate) && lowestTierRate < lowestPrice) {
          lowestPrice = lowestTierRate;
          priceType = type;
        }
      }
    };
    
    // Helper to check season pricing
    const checkSeasonPricing = (seasonPricing, cruiseType, seasonName) => {
      if (!seasonPricing) return;
      
      // Check fixed rate
      checkPrice(seasonPricing.fixedRate, `${cruiseType} (${seasonName})`);
      
      // Check per guest rate (for semi-private options)
      if (seasonPricing.perGuestRate) {
        checkPrice(seasonPricing.perGuestRate, `${cruiseType} (${seasonName}) per person`);
      }
      
      // Check guest tiers
      checkGuestTiers(seasonPricing.guestTiers, `${cruiseType} (${seasonName})`);
    };
    
    // Check private half day prices
    if (yacht.pricing.private?.halfDay) {
      checkSeasonPricing(yacht.pricing.private.halfDay.lowSeason, 'Private Half Day', 'Low Season');
      checkSeasonPricing(yacht.pricing.private.halfDay.mediumSeason, 'Private Half Day', 'Mid Season');
      checkSeasonPricing(yacht.pricing.private.halfDay.highSeason, 'Private Half Day', 'High Season');
    }
    
    // Check private full day prices
    if (yacht.pricing.private?.fullDay) {
      checkSeasonPricing(yacht.pricing.private.fullDay.lowSeason, 'Private Full Day', 'Low Season');
      checkSeasonPricing(yacht.pricing.private.fullDay.mediumSeason, 'Private Full Day', 'Mid Season');
      checkSeasonPricing(yacht.pricing.private.fullDay.highSeason, 'Private Full Day', 'High Season');
    }
    
    // Check semi-private half day prices
    if (yacht.pricing.semiPrivate?.halfDay) {
      checkSeasonPricing(yacht.pricing.semiPrivate.halfDay.lowSeason, 'Semi-Private Half Day', 'Low Season');
      checkSeasonPricing(yacht.pricing.semiPrivate.halfDay.mediumSeason, 'Semi-Private Half Day', 'Mid Season');
      checkSeasonPricing(yacht.pricing.semiPrivate.halfDay.highSeason, 'Semi-Private Half Day', 'High Season');
    }
    
    // Check semi-private full day prices
    if (yacht.pricing.semiPrivate?.fullDay) {
      checkSeasonPricing(yacht.pricing.semiPrivate.fullDay.lowSeason, 'Semi-Private Full Day', 'Low Season');
      checkSeasonPricing(yacht.pricing.semiPrivate.fullDay.mediumSeason, 'Semi-Private Full Day', 'Mid Season');
      checkSeasonPricing(yacht.pricing.semiPrivate.fullDay.highSeason, 'Semi-Private Full Day', 'High Season');
    }
    
    return lowestPrice === Infinity ? null : { price: lowestPrice, type: priceType };
  };

  // Get lowest price
  const lowestPriceInfo = findLowestPrice();
  const priceLabel = lowestPriceInfo ? `€${lowestPriceInfo.price.toLocaleString()}` : 'Inquire';

  // Get first included item (if any) for a quick preview
  const firstIncludedItem = Array.isArray(yacht.includedItems) && yacht.includedItems.length > 0 
    ? yacht.includedItems[0] 
    : null;

  // Use first image from images array or a placeholder
  const yachtImage = (Array.isArray(yacht.images) && yacht.images.length > 0 ? `https://yacht-server.create-site.gr${yacht.images[0]}` : 'https://placehold.co/600x400');

  // Determine display name - use yacht.name if available, otherwise construct from make/model
  const displayName = `${yacht.builder || ''} ${yacht.name || ''}`.trim() || 'Unnamed Yacht';

  // Determine yacht type/category
  const yachtType = yacht.yachtType || yacht.category || 'N/A';

  return (
    <div
      onClick={handleCardClick}
      className="mx-2 bg-white rounded-lg shadow-md overflow-hidden border border-gray-200 cursor-pointer transform transition-all duration-300 hover:shadow-xl hover:-translate-y-1 flex flex-col h-full"
    >
      <div className="relative">
        <img
          src={yachtImage}
          alt={displayName}
          className="w-full h-60 object-cover"
        />
        {/* Status badge */}
        {yacht.status && (
          <div className={`absolute top-3 right-3 px-2 py-1 rounded-full text-xs font-semibold
            ${yacht.status === 'available' ? 'bg-green-100 text-green-800' : 
              yacht.status === 'maintenance' ? 'bg-yellow-100 text-yellow-800' : 
              'bg-red-100 text-red-800'}`}>
            {yacht.status.charAt(0).toUpperCase() + yacht.status.slice(1)}
          </div>
        )}
      </div>
      
      <div className="p-4 flex flex-col flex-grow">
        <div className="flex justify-between items-start mb-2">
          <h3 className="text-lg font-semibold line-clamp-2 flex-1">{displayName}</h3>
          <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">{yachtType}</span>
        </div>
        
        <p className="text-gray-600 text-sm mb-3 line-clamp-2">{yacht.builder || 'Builder N/A'}</p>
        
        <div className="grid grid-cols-2 gap-x-4 gap-y-2 mb-4 text-sm text-gray-600 flex-grow">
          <div className="flex items-center" title="Guests">
            <FaUserFriends className="mr-2 text-blue-500 text-base" />
            <span>{yacht.guestCapacity || 'N/A'} Guests</span>
          </div>
          <div className="flex items-center" title="Crew">
            <GiCaptainHatProfile className="mr-2 text-blue-500 text-base" />
            <span>{yacht.crewCapacity || 'N/A'} Crew</span>
          </div>
          <div className="flex items-center" title="Length">
            <FaRulerCombined className="mr-2 text-blue-500 text-base" />
            <span>{yacht.specifications?.length || 'N/A'}</span>
          </div>
          <div className="flex items-center" title="Beam">
            <IoMdResize className="mr-2 text-blue-500 text-base" />
            <span>{yacht.specifications?.beam || 'N/A'} Beam</span>
          </div>
          <div className="flex items-center" title="Draft">
            <GiAnchor className="mr-2 text-blue-500 text-base" />
            <span>Draft: {yacht.specifications?.draft || 'N/A'}</span>
          </div>
          <div className="flex items-center" title="Year Built">
            <FaCalendarAlt className="mr-2 text-blue-500 text-base" />
            <span>Built: {yacht.yearBuilt || 'N/A'}</span>
          </div>
          
          {firstIncludedItem && (
            <div className="flex items-center col-span-2" title="Included">
              <FaCheck className="mr-2 text-green-500 text-base" />
              <span className="truncate">Included: {firstIncludedItem}, And More!</span>
            </div>
          )}
        </div>

        <div className="flex justify-between items-center pt-4 border-t border-gray-200 mt-auto">          <div className="flex flex-col">
            {lowestPriceInfo ? (
              <>
                <span className="text-xs text-gray-500">Starting From</span>
                <div className="text-blue-900 font-bold text-lg flex items-center">
                  <FaMoneyBillWave className="text-green-600 mr-1 text-base" />
                  {priceLabel}
                  {lowestPriceInfo.type.toLowerCase().includes("per person") && 
                    <span className="text-xs font-normal text-gray-500 ml-1">/person</span>}
                </div>
                <span className="text-xs text-gray-500 line-clamp-1">{lowestPriceInfo.type}</span>
              </>
            ) : (
              <span className="text-blue-900 font-bold">Contact for Pricing</span>
            )}
          </div>
          
          <button
            onClick={handleButtonClick}
            className="bg-blue-800 text-white px-4 py-2 rounded-md hover:bg-blue-900 transition-colors duration-200 text-sm font-medium shadow-sm hover:shadow-md"
          >
            View Details
          </button>
        </div>
      </div>
    </div>
  );
};

export default YachtCard;
